import { Injectable } from '@angular/core';
import {
  CreateTeamAgentSubstitutionGQL,
  GetActiveTeamSubstitutionsGQL,
  GetActiveTeamSubstitutionsQuery,
  GetTeamAgentsForSubstitutionGQL,
  GetTeamAgentsForSubstitutionQuery,
} from '@generated/graphql';
import { DropdownItem } from '@voyagu/radar-design-system';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AgentSubstitution,
  AgentSubstitutionService,
  toSubstitutionDateTime,
  toSubstitutionEndDateTime,
} from '@app/shared/services/agent-substitution/agent-substitution.service';

export type TeamSubstitutionAgent = NonNullable<
  NonNullable<GetTeamAgentsForSubstitutionQuery['userAuth']>['teamAgentsForSubstitution']
>[number];

/** Local mapper — avoids mapUserToDropdown's AgentNode/UserNode picture nullability mismatch. */
export function mapTeamAgentToDropdown(
  agent: TeamSubstitutionAgent,
): DropdownItem & { value: string; label: string } {
  return {
    value: agent.id,
    label: agent.fullName,
    picture: agent.picture ?? '',
  };
}

export type TeamSubstitution = NonNullable<
  GetActiveTeamSubstitutionsQuery['activeTeamSubstitutions']
>[number];

@Injectable({
  providedIn: 'root',
})
export class AgentSubstitutionTeamService {
  constructor(
    private getTeamAgentsForSubstitutionGQL: GetTeamAgentsForSubstitutionGQL,
    private getActiveTeamSubstitutionsGQL: GetActiveTeamSubstitutionsGQL,
    private createTeamAgentSubstitutionGQL: CreateTeamAgentSubstitutionGQL,
    private agentSubstitutionService: AgentSubstitutionService,
  ) {}

  getTeamAgents(): Observable<TeamSubstitutionAgent[]> {
    return this.getTeamAgentsForSubstitutionGQL
      .fetch({}, { fetchPolicy: 'network-only' })
      .pipe(map(({ data }) => data?.userAuth?.teamAgentsForSubstitution ?? []));
  }

  getActiveTeamSubstitutions(): Observable<TeamSubstitution[]> {
    return this.getActiveTeamSubstitutionsGQL
      .fetch({}, { fetchPolicy: 'network-only' })
      .pipe(map(({ data }) => data?.activeTeamSubstitutions ?? []));
  }

  /**
   * The caller's own open coverage (`activeAgentSubstitution`).
   * `activeTeamSubstitutions` is limited to the sales team, so a holder of
   * team-manage can drop out of that list while their coverage stays active.
   */
  getOwnActive(): Observable<AgentSubstitution | null> {
    return this.agentSubstitutionService.getActive();
  }

  create(input: {
    absentAgentId: string;
    coveringAgentId: string;
    startAt: Date;
    endAt?: Date;
  }): Observable<TeamSubstitution | null> {
    return this.createTeamAgentSubstitutionGQL
      .mutate({
        absentAgentId: input.absentAgentId,
        coveringAgentId: input.coveringAgentId,
        startAt: input.startAt,
        endAt: input.endAt,
      })
      .pipe(map(({ data }) => data?.createTeamAgentSubstitution?.result ?? null));
  }

  end(substitutionId: string) {
    return this.agentSubstitutionService.end(substitutionId);
  }
}

export { toSubstitutionDateTime, toSubstitutionEndDateTime };

/** Absent agent shown for a coverage row the team query did not return. */
export type OwnCoverageAgent = {
  id: string;
  fullName: string;
  picture?: string | null;
};

/**
 * Keep the caller's own open coverage in the active list when the team-scoped
 * query omitted it. Rows already present are left as the backend returned them.
 */
export function includeOwnCoverage(
  rows: TeamSubstitution[],
  own: AgentSubstitution | null,
  absentAgent: OwnCoverageAgent | null,
): TeamSubstitution[] {
  if (!own?.id || rows.some((row) => row.id === own.id)) {
    return rows;
  }

  return [
    {
      id: own.id,
      startAt: own.startAt,
      endAt: own.endAt,
      // The served dates must be carried too: the row this mints is rendered by the same
      // template as a backend row (`row.startDate` / `row.throughDate`), and both fields are
      // OPTIONAL in the generated type — omitting them compiles and silently blanks the dates
      // on exactly the row the team-scoped query left out.
      startDate: own.startDate,
      throughDate: own.throughDate,
      absentAgent: absentAgent
        ? {
            id: absentAgent.id,
            fullName: absentAgent.fullName,
            picture: absentAgent.picture ?? undefined,
          }
        : undefined,
      coveringAgent: own.coveringAgent
        ? {
            id: own.coveringAgent.id,
            fullName: own.coveringAgent.fullName,
          }
        : undefined,
    },
    ...rows,
  ];
}
